<script lang="ts">
import { ref, watch, computed, useTemplateRef } from 'vue';
import type { ComponentProps } from '../../types/helper';
import Textfield1XP from '../Textfield1XP/Textfield1XP.vue';
import Button from '../Button1XP/Button1XP.vue';
import Icon from '../Icon/Icon.vue';
import Modal from './components/Modal/Modal.vue';
import Hits from './components/Hits/Hits.vue';
import Group from './components/Group/Group.vue';
import { debounce } from './utils/debounce';
import { getResults } from './utils/getResults';
import { getLatestSearches, setLatestSearches } from './utils/getLatestSearches';
import { isDesktop, isWidescreen } from '../../utils/breakpoints';
import { useStoreConfiguration } from '../../store/configuration/configuration';
import { useStoreTranslations } from '../../store/translations/translations.ts';

type Props = {
  placeholder?: ComponentProps<typeof Textfield1XP>['placeholder'];
};

const searchDebounce = 250; // ? debounce user input for 500ms

const componentId = 'search1XP';
</script>

<script setup lang="ts">
defineProps<Props>();

const storeConfiguration = useStoreConfiguration();
const translations = useStoreTranslations();

// * user input

const searchPhrase = ref<string>();
const latestUserInput = ref<string>();

// hits

const results = ref<string[]>([]);
const latestSearches = ref(getLatestSearches());
const hits = computed(() => [...results.value, ...latestSearches.value]);

// * cursor index

const cursorIndex = ref(-1);
const cursorIndexResults = computed(() => cursorIndex.value);
const cursorIndexLatestSearches = computed(() =>
  Math.max(cursorIndexResults.value - results.value.length, -1),
);

// * ui states

const searchPhraseRef = useTemplateRef('searchPhraseRef');
const isOpen = ref(false);

// * user handlers

const handleFocus = (event: Event) => {
  event.stopPropagation();

  window.scrollTo(0, 0);
  document.body.style.overflow = 'hidden';

  isOpen.value = true;
};

const handleCancel = () => {
  cursorIndex.value = -1;
  searchPhrase.value = undefined;
  isOpen.value = false;
  results.value = [];

  searchPhraseRef.value?.inputRef?.blur();
  document.body.style.overflow = '';
};

const handleKeyUp = () => {
  cursorIndex.value = Math.max(cursorIndex.value - 1, -1);
  searchPhrase.value =
    cursorIndex.value > -1 ? hits.value[cursorIndex.value] : latestUserInput.value;
};

const handleKeyDown = () => {
  cursorIndex.value = Math.min(cursorIndex.value + 1, hits.value.length - 1);
  searchPhrase.value = hits.value[cursorIndex.value];
};

const handleSubmit = () => {
  if (!searchPhrase.value) {
    return;
  }

  if (searchPhrase.value.length < 2) {
    return;
  }

  setLatestSearches(searchPhrase.value);
  latestSearches.value = getLatestSearches();

  window.location.href = `${storeConfiguration.config?.pt_search_result_page.route}?wika-search=${searchPhrase.value}`;
};

const handleHitClick = (value: string) => {
  searchPhrase.value = value;
  handleSubmit();
};

// * effect to get result

watch(searchPhrase, async () => {
  if (cursorIndex.value !== -1) {
    return;
  }

  await debounce(async () => {
    results.value = await getResults(searchPhrase.value);
  }, searchDebounce);
});

// * click outside

const outsideClickListener = (event: Event) => {
  const target = event.target as Element;

  if (!target.closest(`#${componentId}`)) {
    handleCancel();
  }
};

watch(isOpen, () => {
  if (isOpen.value) {
    document.addEventListener('click', outsideClickListener);
    if (isDesktop || isWidescreen) {
      document.body.style.maxHeight = '100vh';
    }
  } else {
    document.removeEventListener('click', outsideClickListener);
    document.body.style.maxHeight = '';
  }
});
</script>

<template>
  <button v-if="!isDesktop && !isWidescreen" :class="$style.icon" @click="handleFocus">
    <Icon name="search" size="large" />
  </button>
  <Teleport v-if="isOpen && (isDesktop || isWidescreen)" to="body">
    <div :class="$style.offset" />
  </Teleport>
  <Teleport :disabled="isDesktop || isWidescreen" to="body">
    <div
      v-if="isOpen || isDesktop || isWidescreen"
      :id="componentId"
      :class="isOpen && $style.containerOpen"
    >
      <div :class="$style.userInput">
        <Textfield1XP
          ref="searchPhraseRef"
          :class="$style.textfield"
          :close-label="translations.translations?.['search.clear'] || 'Clear search'"
          is-cancelable
          is-rounded
          :label="translations.translations?.['search.fieldLabel'] || 'Search'"
          :max-length="50"
          :placeholder="placeholder"
          start-icon="search"
          :value="searchPhrase"
          @focus="handleFocus"
          @input="
            (event) => {
              cursorIndex = -1;
              latestUserInput = event.currentTarget.value;
              searchPhrase = event.currentTarget.value;
            }
          "
          @key-down="handleKeyDown"
          @key-enter="handleSubmit"
          @key-esc="handleCancel"
          @key-up="handleKeyUp"
        />

        <Button
          v-if="isOpen"
          :class="$style.cancel"
          size="s"
          :text="translations.translations?.['search.cancel'] || 'Cancel'"
          variant="linkStatic"
          @click="handleCancel"
        />
      </div>
      <Modal :is-open="isOpen">
        <div :class="$style.resultContainer">
          <div :class="['container', $style.container]">
            <Group
              v-if="results.length > 0"
              :label="translations.translations?.['search.results'] || 'Results'"
            >
              <Hits
                :cursor-index="cursorIndexResults"
                :items="results.map((result) => ({ label: result, id: result }))"
                :search-phrase="latestUserInput"
                @click="handleHitClick"
              />
            </Group>

            <Group
              v-if="latestSearches.length > 0"
              :label="translations.translations?.['search.previousSearches'] || 'Latest searches'"
            >
              <Hits
                :cursor-index="cursorIndexLatestSearches"
                :items="
                  [...latestSearches].reverse().map((result) => ({ label: result, id: result }))
                "
                :search-phrase="latestUserInput"
                @click="handleHitClick"
              />
            </Group>
          </div>
        </div>
      </Modal>
    </div>
  </Teleport>
</template>

<style module lang="scss" src="./PageHeaderSearch.scss" />
